// ─── VisualizeTab ─────────────────────────────────────────────────────────────
// Data Analytics → Visualize sub-tab. Matches the XD design:
//   - Toolbar: zoom selector, undo/redo, a strip of chart-type picker buttons,
//     collapse chevrons, and a 3-dot menu (View chart / Download CSV / Print).
//   - A real spreadsheet (react-spreadsheet) whose columns are filled
//     dynamically from the parameters checked in the left Site panel:
//     column A = Datetime, B/C/D… = each selected parameter's values,
//     aligned by timestamp and bounded by the D/W/M/Y date range.
//
// Data is fetched + aligned by useVisualizeData; the chart icons open a popup
// that plots the same data (one trace per parameter).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useMemo, useRef, useEffect } from 'react'
import Spreadsheet from 'react-spreadsheet'
import { MoreVertical, Loader, Download, Printer, FileImage, FileText, FileSpreadsheet } from 'lucide-react'
import { useVisualizeData } from './useVisualizeData'
import ChartPopup from './ChartPopup'

// All chart types shown in a single row — no pagination needed.
const ALL_CHARTS = [
    { id: 'line',       label: 'Line',           icon: LineGlyph },
    { id: 'multi-line', label: 'Multi-line',     icon: MultiLineGlyph },
    { id: 'bar',        label: 'Bar',            icon: BarGlyph },
    { id: 'area',       label: 'Area',           icon: AreaGlyph },
    { id: 'column',     label: 'Column',         icon: ColumnGlyph },
    { id: 'column-grp', label: 'Grouped Column', icon: GroupedColumnGlyph },
    { id: 'h-bar',      label: 'Horizontal Bar', icon: HBarGlyph },
    { id: 'pie',        label: 'Pie',            icon: PieGlyph },
    { id: 'donut',      label: 'Donut',          icon: DonutGlyph },
    { id: 'half-pie',   label: 'Half Pie',       icon: HalfPieGlyph },
    { id: 'radar',      label: 'Radar',          icon: RadarGlyph },
    { id: 'gauge',      label: 'Gauge',          icon: GaugeGlyph },
]

const ZOOM_LEVELS = ['50%', '75%', '100%', '125%', '150%']

// Must match the fixed dimensions in index.css (.dataanalytics-sheet).
const COL_W = 150
const ROW_H = 28
const ROWHDR_W = 44
const MIN_ROWS = 25

function csvEscape(v) {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export default function VisualizeTab({ selectedParameters = [], dateRangeGraph, activePeriod }) {
    const [zoom, setZoom] = useState('100%')
    const [selectedChartType, setSelectedChartType] = useState('line')
    const [chartOpen, setChartOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    const { data, series, loading, error } = useVisualizeData(selectedParameters, dateRangeGraph, activePeriod)

    const zoomFactor = parseInt(zoom, 10) / 100

    // Measure the grid container so we can pad the matrix to fill it (no blank
    // area on the right) like a real spreadsheet — responsive to resize/zoom.
    const sheetRef = useRef(null)
    const [box, setBox] = useState({ w: 0, h: 0 })
    useEffect(() => {
        const el = sheetRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver((entries) => {
            const cr = entries[0].contentRect
            setBox({ w: cr.width, h: cr.height })
        })
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    const paddedData = useMemo(() => {
        if (!data.length) return data
        const dataCols = data.reduce((m, r) => Math.max(m, r.length), 0)
        const fitCols = Math.max(0, Math.floor((box.w / zoomFactor - ROWHDR_W) / COL_W))
        const fitRows = Math.max(0, Math.floor((box.h / zoomFactor - ROW_H) / ROW_H))
        const totalCols = Math.max(dataCols, fitCols, 1)
        const totalRows = Math.max(data.length, fitRows, MIN_ROWS)
        const blank = { value: '', readOnly: true }
        const out = []
        for (let r = 0; r < totalRows; r++) {
            const src = data[r]
            const row = new Array(totalCols)
            for (let c = 0; c < totalCols; c++) row[c] = src && src[c] ? src[c] : blank
            out.push(row)
        }
        return out
    }, [data, box.w, box.h, zoomFactor])

    const downloadCsv = () => {
        const csv = data.map((row) => row.map((cell) => csvEscape(cell?.value)).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'visualize-data.csv'
        a.click()
        URL.revokeObjectURL(url)
        setMenuOpen(false)
    }

    const downloadXls = async () => {
        const { utils, writeFile } = await import('xlsx')
        const wsData = data.map((row) => row.map((cell) => cell?.value ?? ''))
        const ws = utils.aoa_to_sheet(wsData)
        const wb = utils.book_new()
        utils.book_append_sheet(wb, ws, 'Data')
        writeFile(wb, 'visualize-data.xlsx')
        setMenuOpen(false)
    }

    const downloadPng = async () => {
        const { default: html2canvas } = await import('html2canvas')
        const el = sheetRef.current
        if (!el) return
        const canvas = await html2canvas(el, { scale: 2, useCORS: true })
        const url = canvas.toDataURL('image/png')
        const a = document.createElement('a')
        a.href = url
        a.download = 'visualize-data.png'
        a.click()
        setMenuOpen(false)
    }

    const downloadPdf = async () => {
        const { default: html2canvas } = await import('html2canvas')
        const { jsPDF } = await import('jspdf')
        const el = sheetRef.current
        if (!el) return
        const canvas = await html2canvas(el, { scale: 2, useCORS: true })
        const imgData = canvas.toDataURL('image/png')
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width / 2, canvas.height / 2] })
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2)
        pdf.save('visualize-data.pdf')
        setMenuOpen(false)
    }

    const hasParams = selectedParameters.length > 0

    return (
        <div className="flex flex-col gap-3 flex-1 px-4 pb-4 min-h-0" style={{ fontFamily: "'Open Sans', sans-serif" }}>
            {/* Toolbar — XD: #F1F2F4 background, 8px radius */}
            <div className="flex items-center gap-3 bg-[#F1F2F4] rounded-lg px-3 py-2">
                {/* Left: zoom */}
                <div className="flex items-center gap-2">
                    <select
                        value={zoom}
                        onChange={(e) => setZoom(e.target.value)}
                        className="bg-gray-50 border border-gray-200 px-2 py-1 rounded text-xs text-gray-700 hover:bg-gray-100 focus:outline-none"
                    >
                        {ZOOM_LEVELS.map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                </div>

                {/* Center: all chart-type buttons in one row */}
                <div className="flex-1 flex items-center justify-center gap-1.5">
                    {ALL_CHARTS.map(({ id, label, icon: Glyph }) => {
                        const active = selectedChartType === id
                        return (
                            <button
                                key={id}
                                type="button"
                                title={label}
                                onClick={() => { setSelectedChartType(id); setChartOpen(true) }}
                                className={`w-14 h-11 flex items-center justify-center rounded transition border ${
                                    active ? 'bg-[#E8F1F8] border-[#358ECD]' : 'bg-white border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <Glyph active={active} />
                            </button>
                        )
                    })}
                </div>

                {/* Right: overflow menu */}
                <div className="flex items-center gap-1 relative">
                    <button type="button" onClick={() => setMenuOpen((o) => !o)} className="p-1 text-gray-500 hover:bg-gray-100 rounded">
                        <MoreVertical className="w-4 h-4" />
                    </button>
                    {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-20 w-56 py-1 text-sm">
                            <button onClick={downloadPdf} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                                <FileText className="w-4 h-4 text-gray-500 shrink-0" /> Download PDF
                            </button>
                            <button onClick={downloadPng} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                                <FileImage className="w-4 h-4 text-gray-500 shrink-0" /><span className="whitespace-nowrap">Download PNG Image</span>
                            </button>
                            <button onClick={downloadCsv} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                                <Download className="w-4 h-4 text-gray-500 shrink-0" /> Download CSV
                            </button>
                            <button onClick={downloadXls} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                                <FileSpreadsheet className="w-4 h-4 text-gray-500 shrink-0" /> Download XLS
                            </button>
                            <button onClick={() => { window.print(); setMenuOpen(false) }} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                                <Printer className="w-4 h-4 text-gray-500 shrink-0" /> Print
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {error && (
                <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">{error}</div>
            )}

            {/* Spreadsheet */}
            <div ref={sheetRef} className="flex-1 bg-white border border-gray-200 overflow-auto min-h-0">
                {!hasParams ? (
                    <div className="h-full flex items-center justify-center text-sm text-gray-400 p-8 text-center">
                        Select one or more parameters from the Site panel to fill the sheet.
                    </div>
                ) : loading ? (
                    <div className="h-full flex items-center justify-center gap-2 text-sm text-gray-500">
                        <Loader className="w-4 h-4 animate-spin" /> Loading data…
                    </div>
                ) : (
                    <div style={{ zoom: zoomFactor }} className="dataanalytics-sheet">
                        <Spreadsheet data={paddedData} onChange={() => {}} />
                    </div>
                )}
            </div>

            <ChartPopup open={chartOpen} onClose={() => setChartOpen(false)} chartType={selectedChartType} series={series} />
        </div>
    )
}

// ──────────────────────────────────────────────────────────────────────────────
// Chart-type glyphs — small SVGs to keep visual fidelity without another icon set.
// ──────────────────────────────────────────────────────────────────────────────

// XD palette — icons are two-tone blue + green (gauges use R/Y/G), matching the
// Adobe XD chart-toolbar glyphs. Active state is shown by the button border, so
// glyph colours are fixed.
const BLUE = '#358ECD'
const GREEN = '#5EA64D'
const G_RED = '#E0563A'
const G_YEL = '#E8B23A'
const ORANGE = '#E0A030'

function LineGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20" fill="none">
            <path d="M2 14 L7 9 L12 12 L18 4 L22 8" stroke={BLUE} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
function MultiLineGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20" fill="none">
            <path d="M2 13 L7 8 L12 11 L18 4 L22 7" stroke={BLUE} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 17 L7 14 L12 16 L18 11 L22 14" stroke={GREEN} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
// Grouped columns — green + blue pairs (XD bar icon).
function BarGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <rect x="3"  y="9"  width="3" height="9"  fill={GREEN} />
            <rect x="6.5" y="6" width="3" height="12" fill={BLUE} />
            <rect x="12" y="11" width="3" height="7"  fill={GREEN} />
            <rect x="15.5" y="4" width="3" height="14" fill={BLUE} />
        </svg>
    )
}
// Area — green fill under a blue line.
function AreaGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <path d="M2 16 L7 9 L12 12 L18 4 L22 8 L22 18 L2 18 Z" fill={GREEN} fillOpacity="0.45" />
            <path d="M2 16 L7 9 L12 12 L18 4 L22 8" fill="none" stroke={BLUE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
// Single-series columns (XD green columns).
function ColumnGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <rect x="3"  y="10" width="3.2" height="8"  fill={GREEN} />
            <rect x="8"  y="6"  width="3.2" height="12" fill={GREEN} />
            <rect x="13" y="11" width="3.2" height="7"  fill={GREEN} />
            <rect x="18" y="4"  width="3.2" height="14" fill={GREEN} />
        </svg>
    )
}
// Grouped columns — blue + green (XD).
function GroupedColumnGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <rect x="2"  y="9"  width="2.6" height="9"  fill={BLUE} />
            <rect x="5"  y="6"  width="2.6" height="12" fill={GREEN} />
            <rect x="10" y="11" width="2.6" height="7"  fill={BLUE} />
            <rect x="13" y="8"  width="2.6" height="10" fill={GREEN} />
            <rect x="18" y="5"  width="2.6" height="13" fill={BLUE} />
            <rect x="21" y="3"  width="2.6" height="15" fill={GREEN} />
        </svg>
    )
}
// Horizontal bars (XD green).
function HBarGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <rect x="2" y="3"  width="13" height="3" rx="1" fill={GREEN} />
            <rect x="2" y="8.5" width="20" height="3" rx="1" fill={GREEN} />
            <rect x="2" y="14" width="9"  height="3" rx="1" fill={GREEN} />
        </svg>
    )
}
// Two-tone pie (blue + green).
function PieGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <circle cx="12" cy="10" r="8" fill={GREEN} />
            <path d="M12 10 L12 2 A8 8 0 0 1 19.4 12.6 Z" fill={BLUE} />
        </svg>
    )
}
// Two-tone donut (blue + green ring).
function DonutGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <circle cx="12" cy="10" r="6.5" fill="none" stroke={GREEN} strokeWidth="4" />
            <path d="M12 3.5 A6.5 6.5 0 0 1 18.5 10" fill="none" stroke={BLUE} strokeWidth="4" />
        </svg>
    )
}
// Pie variant — blue + green with an exploded slice.
function HalfPieGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <circle cx="11" cy="10" r="7.5" fill={BLUE} />
            <path d="M11 10 L11 2.5 A7.5 7.5 0 0 1 17.5 6.5 Z" fill={GREEN} transform="translate(1.2,-0.8)" />
        </svg>
    )
}
// Radar / spider — grey grid with a coloured polygon + accent points.
function RadarGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <polygon points="12,2 20,8 17,17 7,17 4,8" fill="none" stroke="#CBD5E1" strokeWidth="1" />
            <polygon points="12,6 16,9 14.5,14 9.5,14 8,9" fill={BLUE} fillOpacity="0.30" stroke={BLUE} strokeWidth="1.3" />
            <circle cx="12" cy="2" r="1.3" fill={GREEN} />
            <circle cx="20" cy="8" r="1.3" fill={ORANGE} />
            <circle cx="4" cy="8" r="1.3" fill={G_RED} />
        </svg>
    )
}
// Speedometer gauge — green / yellow / red arc with a needle.
function GaugeGlyph() {
    return (
        <svg width="30" height="25" viewBox="0 0 24 20">
            <path d="M4 16 A8 8 0 0 1 8 8.1" fill="none" stroke={GREEN} strokeWidth="2.4" strokeLinecap="round" />
            <path d="M8 8.1 A8 8 0 0 1 16 8.1" fill="none" stroke={G_YEL} strokeWidth="2.4" />
            <path d="M16 8.1 A8 8 0 0 1 20 16" fill="none" stroke={G_RED} strokeWidth="2.4" strokeLinecap="round" />
            <line x1="12" y1="16" x2="15.5" y2="9.5" stroke="#444" strokeWidth="1.4" strokeLinecap="round" />
            <circle cx="12" cy="16" r="1.4" fill="#444" />
        </svg>
    )
}
